/**
 * graphicsRedrawQueue — the end-of-frame half of PixiGraphics' redraw dedupe.
 *
 * `PixiGraphics.clear()` no longer clears the Pixi Graphics on the spot: it
 * rewinds a cursor and compares the next draw calls with the ones already
 * drawn, so a consumer that redraws the same shapes every frame leaves the
 * Pixi Graphics untouched (no re-tessellation, and no rebuild of the whole
 * stage's instruction set — see PixiGraphics). A frame that draws FEWER
 * shapes than the last one is only known once the frame is over, so `clear()`
 * queues the graphics here and PixiOverlayHost flushes the queue at the start
 * of every `renderer.render`, before anything is drawn to the screen.
 *
 * A separate module (not a static on PixiGraphics) because PixiOverlayHost
 * owns the flush and PixiGraphics already imports PixiOverlayHost.
 */

export interface RedrawFinalizer {
  /** Drop the shapes the frame that just ended did not redraw. */
  finalizeRedraw(): void;
}

const pending = new Set<RedrawFinalizer>();

export function queueRedrawFinalize(graphics: RedrawFinalizer): void {
  pending.add(graphics);
}

export function cancelRedrawFinalize(graphics: RedrawFinalizer): void {
  pending.delete(graphics);
}

/** Called once per rendered frame, before the scene is drawn. */
export function flushGraphicsRedraws(): void {
  if (pending.size === 0) return;
  for (const graphics of pending) graphics.finalizeRedraw();
  pending.clear();
}

/** Test/diagnostic: graphics waiting for the end-of-frame flush. */
export function pendingRedrawCount(): number {
  return pending.size;
}
